// Privacy & Sharing v1 settings page.
//
// LOCAL-ONLY: choices live in localStorage via src/lib/privacy-prefs.ts.
// No server call. UI copy must NEVER imply server-side enforcement.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { ChevronLeft, Shield } from "lucide-react";
import { toast } from "sonner";

import { pageMeta } from "@/lib/seo";
import { getMe } from "@/lib/profile.functions";
import { RoavaPage, RoavaCard, PrimaryButton } from "@/components/roava";
import {
  DEFAULT_PRIVACY_PREFS,
  PRIVACY_DISCLOSURE,
  getPrivacyPrefs,
  setPrivacyPrefs,
  type PhotoSharing,
  type PrivacyPrefs,
  type ProfileSharing,
  type RouteSharing,
} from "@/lib/privacy-prefs";
import { hapticSelection, hapticSuccess } from "@/lib/native/haptics";

export const Route = createFileRoute("/_authenticated/settings/privacy")({
  head: () =>
    pageMeta({
      title: "Privacy & Sharing — Roava",
      description:
        "Roava separates organizer visibility from group visibility. Manage your route, photo, and profile sharing defaults.",
      path: "/settings/privacy",
    }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const meFn = useServerFn(getMe);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => meFn({}) });
  const userId = me?.id ?? null;

  const [prefs, setPrefs] = useState<PrivacyPrefs>(DEFAULT_PRIVACY_PREFS);

  useEffect(() => {
    setPrefs(getPrivacyPrefs(userId));
  }, [userId]);

  function updatePref<K extends keyof PrivacyPrefs>(key: K, value: PrivacyPrefs[K]) {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setPrivacyPrefs(userId, next);
    void hapticSelection();
  }

  function handleDone() {
    void hapticSuccess();
    toast.success(t("privacy_settings.saved_toast"));
    nav({ to: "/profile" });
  }

  return (
    <RoavaPage title={t("privacy_settings.page_title")} subtitle={t("privacy_settings.page_subtitle")} back={{ to: "/profile" }}>
      <div className="space-y-5">
        {/* Intro */}
        <RoavaCard title={t("privacy_settings.how_title")}>
          <p className="text-sm text-[var(--roava-text-muted)] leading-relaxed">
            {t("privacy_settings.how_intro")}
          </p>
          <div className="mt-3 grid gap-2 text-[13px] leading-relaxed">
            <div className="rounded-xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 px-3 py-2.5">
              <p className="font-medium text-[var(--roava-text)]">{t("privacy_settings.organizer_vis_title")}</p>
              <p className="text-[var(--roava-text-muted)]">
                {t("privacy_settings.organizer_vis_body")}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 px-3 py-2.5">
              <p className="font-medium text-[var(--roava-text)]">{t("privacy_settings.group_vis_title")}</p>
              <p className="text-[var(--roava-text-muted)]">
                {t("privacy_settings.group_vis_body")}
              </p>
            </div>
          </div>
          <p className="mt-3 text-[12px] text-[var(--roava-text-muted)]/80">
            {t("privacy_settings.footer_note")} {PRIVACY_DISCLOSURE}
          </p>
        </RoavaCard>

        {/* Route sharing */}
        <RadioCard<RouteSharing>
          title={t("privacy_settings.route_title")}
          helper={t("privacy_settings.route_helper")}
          value={prefs.routeSharing}
          onChange={(v) => updatePref("routeSharing", v)}
          options={[
            { value: "organizers", label: t("privacy_settings.route_organizers"), recommended: true },
            { value: "group", label: t("privacy_settings.route_group") },
            { value: "private", label: t("privacy_settings.route_private") },
          ]}
        />

        {/* Photo sharing */}
        <RadioCard<PhotoSharing>
          title={t("privacy_settings.photo_title")}
          helper={t("privacy_settings.photo_helper")}
          value={prefs.photoSharing}
          onChange={(v) => updatePref("photoSharing", v)}
          options={[
            { value: "group", label: t("privacy_settings.photo_group"), recommended: true },
            { value: "organizers", label: t("privacy_settings.photo_organizers") },
            { value: "private", label: t("privacy_settings.photo_private") },
          ]}
        />

        {/* Profile sharing */}
        <RadioCard<ProfileSharing>
          title={t("privacy_settings.profile_title")}
          helper={t("privacy_settings.profile_helper")}
          value={prefs.profileSharing}
          onChange={(v) => updatePref("profileSharing", v)}
          options={[
            { value: "group", label: t("privacy_settings.profile_group"), recommended: true },
            { value: "organizers", label: t("privacy_settings.profile_organizers") },
          ]}
        />

        {/* Footer */}
        <RoavaCard>
          <div className="flex items-start gap-2 text-[12px] text-[var(--roava-text-muted)]">
            <Shield className="size-3.5 mt-0.5 shrink-0" aria-hidden />
            <p>{PRIVACY_DISCLOSURE}</p>
          </div>
          <div className="mt-3">
            <PrimaryButton onClick={handleDone}>{t("privacy_settings.done")}</PrimaryButton>
          </div>
        </RoavaCard>

        <div className="pt-1">
          <button
            type="button"
            onClick={() => nav({ to: "/profile" })}
            className="inline-flex items-center gap-1 text-sm font-medium text-[var(--roava-text-muted)] hover:text-[var(--roava-text)] transition-colors"
          >
            <ChevronLeft className="size-4" />
            {t("privacy_settings.back_to_profile")}
          </button>
        </div>
      </div>
    </RoavaPage>
  );
}


interface RadioOption<T extends string> {
  value: T;
  label: string;
  recommended?: boolean;
}

function RadioCard<T extends string>({
  title,
  helper,
  value,
  onChange,
  options,
}: {
  title: string;
  helper: string;
  value: T;
  onChange: (v: T) => void;
  options: RadioOption<T>[];
}) {
  const { t } = useTranslation();
  return (
    <RoavaCard title={title}>
      <div className="grid gap-2">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`flex items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                active
                  ? "border-[var(--roava-blue)] bg-[var(--roava-blue)]/10"
                  : "border-[var(--roava-border)] bg-[var(--roava-card)]/40 hover:border-[var(--roava-text-muted)]/40"
              }`}
              aria-pressed={active}
            >
              <span
                className={`grid place-items-center size-5 rounded-full border ${
                  active
                    ? "border-[var(--roava-blue)] bg-[var(--roava-blue)]"
                    : "border-[var(--roava-text-muted)]/60 bg-transparent"
                }`}
                aria-hidden
              >
                {active && <span className="size-2 rounded-full bg-white" />}
              </span>
              <span className="flex-1 min-w-0 text-sm font-medium text-[var(--roava-text)]">
                {opt.label}
              </span>
              {opt.recommended && (
                <span className="text-[10px] uppercase tracking-wide text-[var(--roava-text-muted)] font-semibold">
                  {t("privacy_settings.recommended")}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[12.5px] text-[var(--roava-text-muted)] leading-relaxed">
        {helper}
      </p>
    </RoavaCard>
  );
}
